import { useEffect, useState } from "react";
import api, { errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Eraser, ShieldAlert } from "lucide-react";

/** Admin danger zone: wipe operational data for company handover (keeps chart of accounts, clients, departments, settings, admins). */
export function HandoverSection({ t }) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [emails, setEmails] = useState("osceres92@gmail.com, oswaldo.cereceres@oomining.com");
  const [keepClass, setKeepClass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) api.get("/system/handover/preview").then((r) => setPreview(r.data)).catch(() => {}); }, [open]);

  const run = async () => {
    if (!window.confirm("Esta acción borra TODOS los datos operativos. Se crea un respaldo antes. ¿Continuar?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/system/handover/reset", {
        confirm, keep_classifications: keepClass,
        keep_user_emails: emails.split(/[,\n;]/).map((e) => e.trim()).filter(Boolean),
      });
      setResult(data);
      toast.success("Sistema en blanco. Respaldo: " + data.backup.name);
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const toDelete = (preview?.collections || []).filter((c) => c.action === "borrar" && c.count > 0);
  const totalDocs = toDelete.reduce((a, c) => a + c.count, 0);

  return (
    <div className="border border-destructive/40 bg-destructive/5 p-5 mb-4" data-testid="handover-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-destructive mb-1 flex items-center gap-1"><ShieldAlert size={12} /> Zona de entrega</div>
          <div className="text-[11px] text-muted-foreground max-w-2xl">Deja el sistema en blanco para entregarlo a la empresa: borra artículos, inventario, Cardex, requisiciones, OCs, vales, OTs, equipos, proveedores, personal, usuarios de prueba y folios. <b>Conserva</b> plan de cuentas, clientes, departamentos contratista, configuración de empresa/correo y los administradores indicados. Se genera un respaldo antes.</div>
        </div>
        <Button size="sm" variant={open ? "outline" : "destructive"} className="rounded-none text-xs" onClick={() => setOpen(!open)} data-testid="handover-toggle">
          <Eraser size={12} className="mr-1" /> {open ? "Cerrar" : "Dejar en blanco…"}
        </Button>
      </div>
      {open && (
        <div className="mt-4 space-y-3" data-testid="handover-panel">
          {preview && (
            <div className="text-[11px]">
              <div className="font-bold mb-1">Se borrarán {totalDocs.toLocaleString("es-MX")} registros en {toDelete.length} colecciones:</div>
              <div className="flex flex-wrap gap-1" data-testid="handover-preview">
                {toDelete.map((c) => <span key={c.collection} className="border px-1.5 py-0.5 bg-background">{c.collection} <b>{c.count}</b></span>)}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">Usuarios a conservar (correos, separados por coma)</Label>
              <textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={2} data-testid="handover-emails"
                className="w-full border bg-background p-2 text-xs rounded-none" />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="handover-keep-class">
                <input type="checkbox" checked={keepClass} onChange={(e) => setKeepClass(e.target.checked)} /> Conservar clasificaciones de artículos (Familia / Grupo / Subgrupo)
              </label>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold uppercase tracking-widest">Escribe <span className="font-mono text-destructive">DEJAR EN BLANCO</span> para confirmar</Label>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none h-8 text-xs font-mono" data-testid="handover-confirm" />
              </div>
            </div>
          </div>
          <Button variant="destructive" className="rounded-none font-bold" disabled={busy || confirm.trim().toUpperCase() !== "DEJAR EN BLANCO"} onClick={run} data-testid="handover-run">
            {busy ? "Borrando…" : "Dejar el sistema en blanco"}
          </Button>
          {result && (
            <div className="text-[11px] border bg-background p-2" data-testid="handover-result">
              Listo. Respaldo <b>{result.backup.name}</b> · usuarios eliminados {result.removed_users} · conservados: {result.accounts} subcuentas, {result.clients} clientes, {result.departments} departamentos, {result.kept_users.length} usuarios.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
